import { useGameStore } from '../store';
import { playToggle, ensureAudioContext } from '../sounds';
import { trackEvent } from '../analytics';
import { MultiToggle } from './ui';
import type { PuzzleDifficulty } from '../types';

const COUNT_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 3, label: '3' },
  { value: 5, label: '5' },
  { value: -1, label: '\u221E' },
];

const DIFFICULTY_OPTIONS: { value: PuzzleDifficulty; label: string }[] = [
  { value: 'casual', label: 'Casual' },
  { value: 'advanced', label: 'Advanced' },
];

interface LevelRulesProps {
  /** True when actively playing — locks all rules. */
  isPlaying?: boolean;
}

export function LevelRules({ isPlaying = false }: LevelRulesProps) {
  const trainSettings = useGameStore((s) => s.trainSettings);
  const saveTrainSettings = useGameStore((s) => s.saveTrainSettings);

  return (
    <>
    <div className="top-bar-settings-section">
      <div className="top-bar-settings-section-title">Difficulty</div>
      <div
        className={`top-bar-setting-row top-bar-setting-count${
          isPlaying ? ' top-bar-setting-row--locked' : ''
        }`}
      >
        <div className="top-bar-setting-info">
          <span className="top-bar-setting-label">Puzzle Complexity</span>
          <span className="top-bar-setting-desc">How tricky the logic is</span>
        </div>
        <MultiToggle
          options={DIFFICULTY_OPTIONS}
          value={trainSettings.difficulty}
          onChange={(v) => {
            ensureAudioContext();
            playToggle();
            saveTrainSettings({ ...trainSettings, difficulty: v });
            trackEvent('difficulty_change', { value: v });
          }}
          label="Difficulty"
          disabled={isPlaying}
        />
      </div>
    </div>

    <div className="top-bar-settings-section">
      <div className="top-bar-settings-section-title">Level Rules</div>
      <div className="top-bar-settings-section-subtitle">
        {isPlaying
          ? 'Locked during play \u2014 set before starting.'
          : 'Applied when you start the next puzzle.'}
      </div>

      {/* Strict mode toggle */}
      {isPlaying ? (
        <div
          className="top-bar-setting-row top-bar-setting-row--locked"
          aria-disabled="true"
        >
          <div className="top-bar-setting-info">
            <span className="top-bar-setting-label">Strict Mode</span>
            <span className="top-bar-setting-desc">
              Flag wrong fills and count mistakes
            </span>
          </div>
          <div
            className={`top-bar-setting-toggle${
              trainSettings.strict ? ' top-bar-setting-toggle--on' : ''
            }`}
            aria-hidden="true"
          >
            <div className="top-bar-setting-knob" />
          </div>
        </div>
      ) : (
        <button
          className="top-bar-setting-row"
          onClick={() => {
            ensureAudioContext();
            playToggle();
            saveTrainSettings({
              ...trainSettings,
              strict: !trainSettings.strict,
            });
            trackEvent('strict_toggle', {
              enabled: !trainSettings.strict,
            });
          }}
        >
          <div className="top-bar-setting-info">
            <span className="top-bar-setting-label">Strict Mode</span>
            <span className="top-bar-setting-desc">
              Flag wrong fills and count mistakes
            </span>
          </div>
          <div
            className={`top-bar-setting-toggle${
              trainSettings.strict ? ' top-bar-setting-toggle--on' : ''
            }`}
            aria-hidden="true"
          >
            <div className="top-bar-setting-knob" />
          </div>
        </button>
      )}

      {/* Count-based level rules */}
      <div
        className={`top-bar-setting-row top-bar-setting-count${
          isPlaying ? ' top-bar-setting-row--locked' : ''
        }`}
      >
        <div className="top-bar-setting-info">
          <span className="top-bar-setting-label">Hints</span>
          <span className="top-bar-setting-desc">Reveal a selected cell</span>
        </div>
        <MultiToggle
          options={COUNT_OPTIONS}
          value={trainSettings.hintCount}
          onChange={(v) => {
            playToggle();
            saveTrainSettings({ ...trainSettings, hintCount: v });
            trackEvent('hint_count_change', { value: v });
          }}
          label="Hints"
          disabled={isPlaying}
        />
      </div>
      <div
        className={`top-bar-setting-row top-bar-setting-count${
          isPlaying ? ' top-bar-setting-row--locked' : ''
        }`}
      >
        <div className="top-bar-setting-info">
          <span className="top-bar-setting-label">Checks</span>
          <span className="top-bar-setting-desc">Validate your progress</span>
        </div>
        <MultiToggle
          options={COUNT_OPTIONS}
          value={trainSettings.validateCount}
          onChange={(v) => {
            playToggle();
            saveTrainSettings({ ...trainSettings, validateCount: v });
            trackEvent('validate_count_change', { value: v });
          }}
          label="Checks"
          disabled={isPlaying}
        />
      </div>
    </div>
    </>
  );
}
